import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import {
    BehaviorSubject,
    map,
    Observable,
    startWith,
    Subject,
    switchMap,
    takeUntil
} from 'rxjs';
import { ExtendedChartOptions } from '../../../../../shared/bb-charts/bb-chart/bb-chart.interfaces';
import { BbChartsModule } from '../../../../../shared/bb-charts/bb-charts.module';
import { UserSettingsQuery } from '../../../../../features/user-settings/state/user-settings.query';
import { SbChart } from '../../../../../shared/bb-charts/bb-chart/bb-chart.component';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatIconModule } from '@angular/material/icon';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
    SbBreadcrumbs,
    SbBreadcrumbsComponent
} from '../../../../../shared/components/page/sb-breadcrumbs/sb-breadcrumbs.component';
import { SbPeriodPickerComponent } from '../../../../../shared/components/form/sb-period-picker/sb-period-picker.component';
import { startOfYear, format } from 'date-fns';
import { combineQueries } from '@datorama/akita';
import { AcctsQuery } from '../../../../../shared/state/accts/accts.query';
import {
    distinctUntilChanged,
    filter,
    shareReplay,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { Data } from 'billboard.js/types/options';
import { TranslateService } from '@ngx-translate/core';
import { CLDRService } from '../../../../../shared/services/cldr/cldr.service';
import { SbGenericTypeSelectComponent } from '../../../accounting/components/sb-generic-type-select/sb-generic-type-select.component';
import { DataItem } from 'billboard.js';
import {
    SbKeyMetrics,
    SbKeyMetricsComponent
} from '../../../../../shared/components/sb-key-metrics/sb-key-metrics.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { SbProTipComponent } from '../../../../../shared/components/sb-pro-tip/sb-pro-tip.component';
import { ValueBridgeService } from '../../../../../shared/state/value-bridge/sb-value-bridge.service';
import { GraphDataItem } from '../../../../../shared/state/value-bridge/sb-value-bridge.model';
import { LegalEntityQuery } from '../../../../admin/state/legal-entities/legal-entity.query';

@Component({
    standalone: true,
    imports: [
        CommonModule,
        BbChartsModule,
        MatFormFieldModule,
        ReactiveFormsModule,
        MatSelectModule,
        MatIconModule,
        TablerIconsModule,
        SbPeriodPickerComponent,
        SbBreadcrumbsComponent,
        SbGenericTypeSelectComponent,
        SbKeyMetricsComponent,
        SbProTipComponent
    ],
    selector: 'sb-value-bridge',
    templateUrl: './sb-value-bridge.component.html',
    styleUrls: ['./sb-value-bridge.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SbValueBridgeComponent implements OnInit, OnDestroy {
    get fitHeightToParent(): boolean {
        return this._fitHeightToParent;
    }

    @Input() set fitHeightToParent(value: boolean) {
        this._fitHeightToParent = value;
    }
    get group$() {
        return this._group$.asObservable().pipe(distinctUntilChanged(), shareReplay(1));
    }

    get group() {
        return this._group$.value;
    }

    set group(value: string) {
        this._router.navigate([], {
            queryParams: { valueBridgeGroup: value },
            queryParamsHandling: 'merge'
        });
        this._group$.next(value);
    }

    get chartRef() {
        return this._chartRef;
    }

    set chartRef(chartRef: SbChart) {
        this._chartRef = chartRef;
    }
    get isLoading$() {
        return this._isLoading$
            .asObservable()
            .pipe(distinctUntilChanged(), shareReplay(1));
    }

    set isLoading(value: boolean) {
        this._isLoading$.next(value);
    }

    private _chartRef: SbChart;
    private _isLoading$ = new BehaviorSubject(true);
    private _unsubscribe$: Subject<void> = new Subject();
    private _fitHeightToParent: boolean = false;

    constructor(
        private readonly _service: ValueBridgeService,
        private readonly _settings: UserSettingsQuery,
        private readonly _accountsQuery: AcctsQuery,
        private readonly _translate: TranslateService,
        private readonly _cldrService: CLDRService,
        private readonly _router: Router,
        private readonly _route: ActivatedRoute,
        private readonly _routerQuery: RouterQuery,
        private readonly _legalEntityQuery: LegalEntityQuery
    ) {}
    periodCtrl = new FormControl({
        fromDate: startOfYear(new Date()),
        toDate: new Date()
    });

    period$ = this.periodCtrl.valueChanges.pipe(
        startWith(this.periodCtrl.value),
        shareReplay(1)
    );

    typeOptions = [
        {
            value: 'asset_type',
            label: 'Asset type'
        },
        {
            value: 'portfolio',
            label: 'Account'
        }
    ];
    typeCtrl = new FormControl<(typeof this.typeOptions)[0]>(this.typeOptions[0]);
    type$ = this.typeCtrl.valueChanges.pipe(
        tap((value) => {
            // reset group when type changes
            this.group = null;
        }),
        startWith(this.typeCtrl.value),
        shareReplay(1)
    );

    /** _group$ - behavior subject for group state */
    private _group$ = new BehaviorSubject<string>(
        this._route.snapshot.queryParamMap.get('valueBridgeGroup') || null
    );

    /** chartOptions - options for the value bridge chart */
    chartOptions: ExtendedChartOptions = {
        legend: { show: false },
        padding: {
            mode: 'fit'
        },
        tooltip: {
            grouped: false,
            onshown: (selectedData: DataItem[]) => {
                const category = selectedData[0].id;
                if (category === 'balance') {
                    // hide the tooltip if the category is balance
                    this.chartRef.$.tooltip.style('display', 'none');
                }
            },
            contents: (d, defaultTitleFormat, defaultValueFormat, color) => {
                const categoryId = d[0].x;
                const seriesId = d[0].id;
                const category = this.chartRef.category(+categoryId);
                const valueChange = d[0].value[1] - d[0].value[0];
                const change = valueChange > 0 ? 'positive' : 'negative';
                const formattedValueChange =
                    this._cldrService.formatCurrency(valueChange);
                const prefix =
                    valueChange > 0 && seriesId !== 'start' && seriesId !== 'end'
                        ? '+'
                        : '';
                return `<dl class="bb-tooltip"><div class="d-flex"><dt class="font-weight-regular">${this._translate.instant(
                    category
                )}</dt><dd class="font-weight-bold ms-2 value-color value-${change}">${prefix}${formattedValueChange}</dd></div></dl>`;
            }
        },
        line: {
            point: false
        },
        bar: {
            front: true,
            width: {
                ratio: 1
            }
        },
        data: {
            type: 'bar',
            onclick: (d, _element) => {
                const clickedGroup = this.chartRef.category(d.index);
                if (this.group || clickedGroup === 'OTHER') {
                    return;
                }
                if (d.index > 0 && d.index < this.chartRef.x().length - 1) {
                    this.group = clickedGroup;
                }
            },
            onover: (d, _element) => {
                if (d.index > 0 && d.index < this.chartRef.x().length - 1) {
                    this.chartRef.$.main.select('rect').style('cursor', 'pointer');
                }
            },
            onout: (d, _element) => {
                if (d.index > 0 && d.index < this.chartRef.x().length - 1) {
                    this.chartRef.$.main.select('rect').style('cursor', null);
                }
            },
            classes: {
                start: 'chart-start-color',
                end: 'chart-end-color',
                positive: 'chart-positive-color',
                negative: 'chart-negative-color'
            },
            labels: {
                format: (value, id, i, texts) => {
                    // if any value is null or 0 (initial value)...
                    if (value[0] === null || value[1] === null || value === 0) {
                        // ...don't show the label
                        return '';
                    }

                    const barValue = -(value[0] - value[1]);
                    // if the value is not a number...
                    if (isNaN(barValue)) {
                        // ...don't show the label
                        return '';
                    }
                    // prefix the label with a '+' if the value is positive
                    const prefix = barValue > 0 && id === 'positive' ? '+' : '';

                    // if balance series...
                    if (id === 'balance') {
                        // ...and if the value is the last balance item...
                        const isLastBalance = i === Array.from(texts || []).length - 2;
                        return isLastBalance
                            ? ''
                            : this._cldrService.formatShortNumber(value[0], [0, 1]);
                    }

                    return `${prefix}${this._cldrService.formatShortNumber(
                        barValue,
                        [0, 1]
                    )}`;
                },
                position: (type, v, id, i, texts) => {
                    // if the label is for the balance series and the y position...
                    if (id === 'balance' && type === 'y' && i > 0 && v[1] !== null) {
                        // ...if value is less than the previous value i.e. negative...
                        const values = this.chartRef.data.values('balance');
                        const isNegative = v[1] < values[i - 1][1];
                        // ...return the y position offset, i.e. render the label bellow the line bar if negative
                        return isNegative ? 22 : -10;
                    }
                    return 0;
                }
            },
            selection: {
                enabled: true
            }
        },
        transition: { duration: 500 },
        axis: {
            x: {
                type: 'category',
                tick: {
                    fit: true,
                    multiline: false,
                    autorotate: true,
                    rotate: 45,
                    culling: false,
                    format: (index, category) => {
                        const isFirst = index === 0;
                        const isLast = index === this.chartRef.x().length - 1;
                        // if the category is the first or last i.e. if the category represents a date...
                        if (isFirst || isLast) {
                            // ...format the date according to the locale
                            return formatDate(
                                new Date(category),
                                'shortDate',
                                this._settings.getGlobalSetting('LOCALE')
                            );
                        }
                        return this._translate.instant(category);
                    }
                },
                clipPath: false
            },
            y: {
                label: {
                    text: `Value change in ${this._settings.getGlobalSetting(
                        'BASE_CURRENCY'
                    )}`,
                    position: 'outer-middle'
                },
                tick: {
                    culling: {
                        max: 5
                    },
                    format: (value: number) => {
                        return this._cldrService.formatShortNumber(value, [0, 1]);
                    }
                }
            }
        }
    };

    /** filteredAccountIds$ - observable for filtered account ids for value bridge
     */
    filteredAccountIds$ = this._accountsQuery.isLoading$.pipe(
        filter((isLoading) => !isLoading), // filter loaded state, i.e. proceed once accounts have loaded
        takeWhile((isLoading) => isLoading, true), // unsubscribe to loading state once it has loaded i.e. no need to get future emissions
        switchMap((_) => this._legalEntityQuery.active$),
        switchMap((active) => {
            return this._accountsQuery
                .selectAccountsByFilter$({
                    ownerId: {
                        in: active.map((entity) => entity.ownerId)
                    }
                })
                .pipe(map((accounts) => accounts.map((account) => account.accountId)));
        }),
        shareReplay(1)
    );

    /** _data$ - observable for data for value bridge
     * @description Gets updated whenever period, type, base currency, accounts or group emits */
    private readonly _data$ = combineQueries([
        this.period$,
        this.type$,
        this._settings.selectGlobalSetting$('BASE_CURRENCY'),
        this.filteredAccountIds$,
        this.group$
    ]).pipe(
        switchMap(
            ([
                { fromDate, toDate },
                { value, label },
                baseCurrency,
                accountIds,
                group
            ]) => {
                this.isLoading = true;
                return this._service.get({
                    baseCurrency,
                    startDate: format(fromDate, 'yyyy-MM-dd'), // TODO: ideally we should support passing locale-agnostic date to the backend
                    endDate: format(toDate, 'yyyy-MM-dd'),
                    chartType: value,
                    accountIds,
                    group
                });
            }
        ),
        shareReplay(1)
    );

    /** chartData$ - observable for chart data for value bridge
     * @description Transform data to fit chart model */
    chartData$ = this._data$.pipe(
        map(({ graph, totals }) => {
            const data: Array<
                GraphDataItem & {
                    start: [number, number]; // chart series for start value
                    end: [number, number]; // chart series for end value
                    positive: [number, number]; // chart series for positive value change
                    negative: [number, number]; // chart series for negative value change
                    valueChange: number; // value change from previous value
                    balance: [number, number]; // chart series for balance value
                }
            > = graph.reduce((acc: typeof data, cur, i) => {
                const previous = i > 0 ? acc[i - 1].valueChange : 0;
                const value = cur.value;
                let valueChange = i === graph.length - 1 ? value : previous + value;
                // setting this to null is required render the chart correctly but will cause the chart to throw a not critical error, setting them to [0,0] would hide the error but causes other side effect
                const nullValue = null;
                let start: [number, number] = [nullValue, nullValue];
                let end: [number, number] = [nullValue, nullValue];
                let positive: [number, number] = [nullValue, nullValue];
                let negative: [number, number] = [nullValue, nullValue];
                let balance;
                if (i === 0) {
                    start = [0, value];
                    balance = [value, value + 1];
                } else if (i === graph.length - 1) {
                    end = [0, value];
                    balance = [nullValue, nullValue];
                } else if (value < 0) {
                    negative = [previous, valueChange];
                    balance = [valueChange - 1, valueChange];
                    //negative = [valueChange, previous]; use if label should be on top
                } else {
                    positive = [previous, valueChange];
                    balance = [valueChange, valueChange + 1];
                }
                const chartItem: (typeof data)[0] = {
                    ...cur,
                    start,
                    end,
                    positive,
                    negative,
                    valueChange,
                    balance
                };
                acc.push(chartItem);
                return acc;
            }, []);
            return <
                Data & {
                    json: Array<(typeof data)[0]>;
                }
            >{
                json: data,
                keys: {
                    x: 'group',
                    value: ['start', 'positive', 'negative', 'end', 'balance']
                },
                groups: [['start', 'positive', 'negative', 'end']]
            };
        }),
        tap((data) => {
            const min = Math.min(...data.json.map((item) => item.valueChange));
            const max = Math.max(...data.json.map((item) => item.valueChange));
            if (!this.chartRef) {
                setTimeout(() => {
                    this.chartRef?.axis.range({ min, max });
                }, 200);
            } else {
                this.chartRef?.axis.range({ min, max });
            }
        }),
        shareReplay(1)
    );

    /** metrics$ - observable for key metrics for value bridge
     * @description Transform data to fit key metrics model, emits when data emits
     */
    metrics$ = this._data$.pipe(
        withLatestFrom(this._settings.selectGlobalSetting$('BASE_CURRENCY')),
        map(([{ graph, totals }, ccy]) => {
            return <SbKeyMetrics>[
                {
                    title: 'Value change',
                    value: totals.absolute_change,
                    description:
                        'Absolute and relative value change during selected period.',
                    prefixPositiveValue: true,
                    ccy,
                    change: Math.round(totals.relative_change * 10000) / 10000,
                    addColor: true,
                    formatStyle: 'short',
                    maximumFractionDigits: 2
                },
                {
                    title: 'Period start',
                    value: graph[0]?.value,
                    description: 'Start market value for selected period.',
                    ccy,
                    maximumFractionDigits: 2
                },
                {
                    title: 'Period end',
                    value: graph[graph.length - 1]?.value,
                    description: 'End market value for selected period.',
                    ccy,
                    maximumFractionDigits: 2
                }
            ];
        })
    );

    /** breadcrumbs$ - observable for breadcrumbs for value bridge
     * @description Emits when type, group or translations emits */
    breadcrumbs$: Observable<SbBreadcrumbs> = combineQueries([
        this.type$,
        this.group$,
        this._translate.onTranslationChange.pipe(
            map(({ lang, translations }) => translations),
            startWith(this._translate.translations || {})
        )
    ]).pipe(
        map(([{ label, value }, group, translations]) => {
            let breadcrumbs: SbBreadcrumbs = [
                {
                    label,
                    path: '.',
                    queryParams: { valueBridgeGroup: null },
                    queryParamsHandling: 'merge'
                }
            ];
            if (group) {
                breadcrumbs = [
                    ...breadcrumbs,
                    {
                        queryParamsHandling: 'merge',
                        label: translations[group] || group,
                        path: null // level is not clickable
                    }
                ];
            }
            return breadcrumbs;
        })
    );

    /** numberOfCategories$ - observable for number of categories for value bridge, used to calculate offset for balance series (waterfall line)
     * @description Emits when chartData emits */
    numberOfCategories$ = this.chartData$.pipe(
        map((data) => data.json.length),
        distinctUntilChanged(),
        shareReplay(1)
    );

    ngOnInit(): void {
        // listen to changes in the group query parameter
        this._routerQuery
            .selectQueryParams('valueBridgeGroup')
            .pipe(
                // filter out null values...
                filter((value) => value == null),
                takeUntil(this._unsubscribe$)
            )
            .subscribe((group) => {
                // reset the group state when the query parameter is null
                this.group = null;
            });
    }

    ngOnDestroy(): void {
        this._unsubscribe$.next();
        this._unsubscribe$.complete();
    }
}
